import { z } from 'zod'

import type { ConceptChipType } from '~/@entities/ConceptChip'
import { fromProblemV2, type ProblemV2Wire, ProblemV2WireSchema } from '~/@entities/Problem'
import type {
  CommonResponseListStudentAchievementResponse,
  CommonResponseListStudentAchievementScoredItem,
  CommonResponseSetStudentAchievementLogResponse,
  ConceptChipAndStudentAchievementDto,
  GroupStudentAchievementByConceptChipResponse,
  GroupStudentAchievementConceptChipResponse,
  GroupStudentAchievementLogConceptChipResponse,
  StudentAchievementAggregateResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type AchievementLevel,
  type ConceptChipAchievement,
  type ConceptChipScoredItem,
  ConceptChipScoredItemSchema,
  type GroupConceptChipAchievement,
  type GroupConceptChipAchievementLog,
  GroupConceptChipAchievementLogSchema,
  GroupConceptChipAchievementSchema,
  type GroupConceptChipDetail,
  GroupConceptChipDetailSchema,
  type StudentConceptChipAchievement,
  type StudentConceptChipAchievementLog,
  StudentConceptChipAchievementLogSchema,
  StudentConceptChipAchievementSchema,
  type StudentsAchievementSummary,
  StudentsAchievementSummarySchema,
} from './schemas'

/***************** 서버 코드 표기의 번역표 *****************/
// 서버는 성취도·유형 난이도를 enum 하나씩 들고 있고(AchievementLevel 의 code, ConceptChipType 의 value·title)
// 응답 DTO 마다 이름과 코드 중 어느 쪽을 싣는지가 다르다. 필드 이름이 그 구분이다.
//   achievementLevel·averageAchievementLevel → WHITE…SMILE  /  levelOfAchievement → 0~5·null
//   conceptChipType → 개념·기본·심화          /  levelOfConceptChip → A·B·C
// 둘을 코드로 싣는 응답은 학생 홈 요약(주간정보·스마일챌린지·최근챌린지)뿐이라 두 표를 함께 여기 둔다.
// 2026-09-19 백엔드 concept/model/type/ConceptChipType.kt·studentHistory/model/type/AchievementLevel.kt 로 확인.
//
// 풀지 않은 칩은 코드가 없어 null 로 오고, 그것이 WHITE 다. 코드 목록과 번역표를 함께 두어
// 한쪽만 늘어나면 satisfies 가 막는다.
const ACHIEVEMENT_LEVEL_CODES = [0, 1, 2, 3, 4, 5] as const
const CONCEPT_CHIP_TYPE_CODES = ['A', 'B', 'C'] as const

const ACHIEVEMENT_LEVEL_BY_CODE = {
  0: 'GRAY',
  1: 'SAD',
  2: 'RED',
  3: 'YELLOW',
  4: 'GREEN',
  5: 'SMILE',
} as const satisfies Record<(typeof ACHIEVEMENT_LEVEL_CODES)[number], AchievementLevel>

const CONCEPT_CHIP_TYPE_BY_CODE = {
  A: '개념',
  B: '기본',
  C: '심화',
} as const satisfies Record<(typeof CONCEPT_CHIP_TYPE_CODES)[number], ConceptChipType>

const ConceptChipTypeCodeSchema = z.enum(CONCEPT_CHIP_TYPE_CODES)
const AchievementLevelCodeSchema = z.literal(ACHIEVEMENT_LEVEL_CODES).nullable()

const decodeAchievementLevel = (code: z.infer<typeof AchievementLevelCodeSchema>) =>
  code === null ? 'WHITE' : ACHIEVEMENT_LEVEL_BY_CODE[code]

// 생성 타입은 levelOfAchievement 를 `?: number` 로 그리지만 실제 와이어는 풀지 않은 칩을 null 로 보낸다.
// 2026-09-19 백엔드 studentHome/model/dto/ConceptChipAndStudentAchievementDto.kt 의 `Int?` 로 확인.
// 코드 번역은 검증 스키마의 transform 이 아니라 toConceptChipAchievement 로 따로 둔다. 운영(lenient)에서 검증이
// 실패해 raw 가 넘어와도 achievementLevel·conceptChipType 이름으로 읽히게 하려는 것이다.
// 학생 홈 응답들이 이 조각을 품어 두 값을 export 한다.
export const ConceptChipAchievementWireSchema = z.object({
  conceptChipId: z.number(),
  conceptId: z.number(),
  conceptName: z.string(),
  littleChapterId: z.number(),
  levelOfAchievement: AchievementLevelCodeSchema,
  levelOfConceptChip: ConceptChipTypeCodeSchema,
})

export const toConceptChipAchievement = ({
  levelOfAchievement,
  levelOfConceptChip,
  ...rest
}: z.output<typeof ConceptChipAchievementWireSchema>): ConceptChipAchievement => ({
  ...rest,
  achievementLevel: decodeAchievementLevel(levelOfAchievement ?? null),
  conceptChipType: CONCEPT_CHIP_TYPE_BY_CODE[levelOfConceptChip],
})

export type ConceptChipAchievementWire = ConceptChipAndStudentAchievementDto

export const adaptConceptChipAchievement = (
  raw: ConceptChipAchievementWire,
): ConceptChipAchievement =>
  toConceptChipAchievement(parseApiResponse(ConceptChipAchievementWireSchema, raw, 'Achievement'))

export const adaptConceptChipAchievements = (
  raw: ConceptChipAchievementWire[],
): ConceptChipAchievement[] =>
  (parseApiResponse(z.array(ConceptChipAchievementWireSchema), raw, 'Achievement') ?? []).map(
    toConceptChipAchievement,
  )

/***************** 학생 한 명의 성취도 *****************/

export const adaptStudentConceptChipAchievements = (
  raw: CommonResponseListStudentAchievementResponse['data'],
): StudentConceptChipAchievement[] =>
  parseApiResponse(z.array(StudentConceptChipAchievementSchema), raw, 'Achievement')

export const adaptStudentConceptChipAchievementLogs = (
  raw: CommonResponseSetStudentAchievementLogResponse['data'],
): StudentConceptChipAchievementLog[] =>
  parseApiResponse(z.array(StudentConceptChipAchievementLogSchema), raw, 'Achievement') ?? []

// 스펙이 Kotlin nullable 을 optional 로만 그린 곳. 서버는 키를 빼지 않고 null 을 싣는다 — 출처 페이지·학생 답·
// 손글씨 노트, 문항의 problemId·이미지 URL 3종. 2026-10-13 백엔드 develop(cc5f60e17) 의
// studentHistory 의 StudentAchievementScoredItem.kt·RecentlyScoredQueueScoredItem.kt 로 확인.
// 다중 셀 답(userAnswers)도 같다 — 2026-10-29 dev 응답에서 단답 행에 userAnswers: null 이 실린 것으로 확인.
export const adaptConceptChipScoredItems = (
  raw: CommonResponseListStudentAchievementScoredItem['data'],
): ConceptChipScoredItem[] =>
  parseApiResponse(z.array(ConceptChipScoredItemSchema), raw, 'Achievement') ?? []

/***************** 여러 학생의 성취도 요약 *****************/

// 평균 등급·표준편차는 아무도 풀지 않은 칩에서 null 이다. 2026-10-13 백엔드 develop(cc5f60e17) 의
// studentHistory 의 StudentAchievementAggregateOnChipQuery.kt 로 확인.
export const adaptStudentsAchievementSummary = (
  raw: StudentAchievementAggregateResponse,
): StudentsAchievementSummary =>
  parseApiResponse(StudentsAchievementSummarySchema, raw, 'Achievement')

/***************** 그룹(학년·반) 성취도 *****************/

const _GroupConceptChipAchievementLogWireSchema = GroupConceptChipAchievementLogSchema.extend({
  repProblem: ProblemV2WireSchema.nullable(),
})

const _GroupConceptChipAchievementWireSchema = GroupConceptChipAchievementSchema.extend({
  repProblem: ProblemV2WireSchema.nullable(),
})

// 대표 문제는 V2 로 와서 검증 뒤에 도메인 모양으로 푼다(Problem adapter 의 fromProblemV2 참고)
const withRepProblem = <T extends { repProblem: ProblemV2Wire | null }>({
  repProblem,
  ...rest
}: T) => ({ ...rest, repProblem: repProblem ? fromProblemV2(repProblem) : null })

// 생성 타입이 achievements 원소를 잘못 그린다 — springdoc 이 중첩 클래스
// GroupStudentAchievementConceptChipResponse.StudentAchievementResponse 를 동명의 최상위 타입과
// 병합해 conceptChipId·latestStudyLog 가 있다고 말하지만 실제 와이어는 studentId·studentName·
// achievementLevel 세 필드뿐이다. 스키마 쪽이 실제 모양이라 입력을 한 자리에서 unknown 으로 넘긴다.
// 2026-09-19 백엔드 studentHistory/controller/response/GroupStudentAchievementConceptChipResponse.kt 로 확인.
const asGroupWire = (raw: GroupStudentAchievementConceptChipResponse[]): unknown => raw

export const adaptGroupConceptChipAchievements = (
  raw: GroupStudentAchievementConceptChipResponse[],
): GroupConceptChipAchievement[] =>
  (
    parseApiResponse(
      z.array(_GroupConceptChipAchievementWireSchema),
      asGroupWire(raw),
      'Achievement',
    ) ?? []
  ).map(withRepProblem)

export const adaptGroupConceptChipAchievementLogs = (
  raw: GroupStudentAchievementLogConceptChipResponse[],
): GroupConceptChipAchievementLog[] =>
  (
    parseApiResponse(z.array(_GroupConceptChipAchievementLogWireSchema), raw, 'Achievement') ?? []
  ).map(withRepProblem)

// 같은 병합 결함이 상세에도 있다. achievements 에 더해 highestCorrectRateProblems·
// highestWrongRateProblems 의 원소가 문제은행 ProblemResponse 로 그려져 있지만, 실제로는 중첩 클래스
// GroupStudentAchievementByConceptChipResponse.ProblemResponse(rank·conceptName·groupCorrectRate·
// 이미지 URL 3종, URL 은 nullable)가 온다.
// 2026-09-19 백엔드 studentHistory/controller/response/GroupStudentAchievementByConceptChipResponse.kt 로 확인.
const asGroupDetailWire = (raw: GroupStudentAchievementByConceptChipResponse): unknown => raw

export const adaptGroupConceptChipDetail = (
  raw: GroupStudentAchievementByConceptChipResponse,
): GroupConceptChipDetail =>
  parseApiResponse(GroupConceptChipDetailSchema, asGroupDetailWire(raw), 'Achievement')
